import { useEffect, useState } from 'react';
import type { Choice, Paragraph, Scenario } from '../data/types';
import styles from './Playthrough.module.css';

interface PlaythroughProps {
  scenario: Scenario;
  currentNodeId: string;
  onChoice: (choice: Choice) => void;
  onComplete: () => void;
  onExit: () => void;
}

export function Playthrough({ scenario, currentNodeId, onChoice, onComplete, onExit }: PlaythroughProps) {
  const node = scenario.nodes[currentNodeId];
  const [revealed, setRevealed] = useState(0);
  const [reduced] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  // Paragraph reveal — runs on every node change
  useEffect(() => {
    if (!node) return;
    if (reduced) {
      setRevealed(node.paragraphs.length);
      return;
    }
    setRevealed(0);
    const timeouts: number[] = [];
    let cumulative = 200;
    node.paragraphs.forEach((p, i) => {
      timeouts.push(window.setTimeout(() => setRevealed(i + 1), cumulative));
      cumulative += p.pause ?? 800;
    });
    return () => timeouts.forEach((t) => window.clearTimeout(t));
  }, [node, reduced]);

  // Derived state — safe with optional chaining for undefined node
  const totalParagraphs = node?.paragraphs.length ?? 0;
  const allRevealed = revealed >= totalParagraphs;
  const choices: Choice[] = node?.choices ?? [];
  const hasChoices = choices.length > 0;
  const isEnd = Boolean(node) && allRevealed && !hasChoices;

  // Completion handler
  useEffect(() => {
    if (!isEnd) return;
    const id = window.setTimeout(onComplete, 1400);
    return () => window.clearTimeout(id);
  }, [isEnd, onComplete]);

  // Keyboard 1..N to select choices
  useEffect(() => {
    if (!allRevealed || !hasChoices) return;
    function onKey(e: KeyboardEvent) {
      const n = Number(e.key);
      if (n >= 1 && n <= choices.length) {
        const choice = choices[n - 1];
        if (choice) onChoice(choice);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allRevealed, hasChoices, choices, onChoice]);

  if (!node) return null;

  return (
    <article className={styles.page}>
      <header className={styles.head}>
        <span className={styles.scenarioTitle}>{scenario.title}</span>
        <button type="button" className={styles.exit} onClick={onExit}>
          save & exit
        </button>
      </header>

      <div className={styles.paragraphs}>
        {node.paragraphs.slice(0, revealed).map((p, i) => (
          <ParagraphView key={`${node.id}-${i}`} paragraph={p} />
        ))}
        {!allRevealed && (
          <button
            type="button"
            className={styles.skipBtn}
            onClick={() => setRevealed(node.paragraphs.length)}
            aria-label="reveal all"
          >
            ⌄
          </button>
        )}
      </div>

      {allRevealed && hasChoices && (
        <div className={styles.choices}>
          {choices.map((choice, i) => (
            <button
              key={i}
              type="button"
              className={styles.choice}
              onClick={() => onChoice(choice)}
            >
              <span className={styles.choiceNum}>{i + 1}</span>
              <span className={styles.choiceText}>{choice.text}</span>
            </button>
          ))}
        </div>
      )}

      {isEnd && (
        <p className={styles.endHint}>(the scenario closes here)</p>
      )}
    </article>
  );
}

function ParagraphView({ paragraph }: { paragraph: Paragraph }) {
  const cls =
    paragraph.speaker === 'them' ? styles.them :
    paragraph.speaker === 'you' ? styles.you :
    styles.narration;
  return <p className={`${styles.paragraph} ${cls}`}>{paragraph.text}</p>;
}
